import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from '../primitives';
import { useDocuments } from '../../api/hooks';
import type { SavedDocument } from '../../api/schemas';

/**
 * Tlačítko „Uložené" do lišty dokumentu.
 *
 * Uložené dokumenty daného typu byly dosud dostupné jen přes Archiv v menu —
 * z formuláře se k nim uživatel nedostal a po uložení netušil, kam zmizely
 * (připomínka „dala jsem uložit, ale v uložených nenašla"). Tohle tlačítko
 * ukáže seznam přímo u dokumentu a otevře vybraný záznam k úpravě
 * (`/app/{docType}?id=…`) nebo jeho PDF náhled (`&pdf=preview`).
 */
export interface SavedDocsButtonProps {
  /** Typ dokumentu = zároveň cesta routy, např. `objednavka`. */
  docType: string;
  /** Nadpis dialogu, např. „Uložené objednávky". */
  title: string;
  /** Id právě otevřeného záznamu — zvýrazní se v seznamu. */
  currentId?: number | null;
  label?: string;
  /**
   * Cesta formuláře, když se liší od `/app/{docType}` (např. trauma plán
   * žije na `/app/bozp/trauma`).
   */
  path?: string;
}

function formatDateTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('cs-CZ');
}

export function SavedDocsButton({
  docType,
  title,
  currentId,
  label = 'Uložené',
  path,
}: SavedDocsButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" leading="📂" onClick={() => setOpen(true)}>
        {label}
      </Button>
      {/* Seznam se stahuje až po otevření — lišta dokumentu ho nemá proč držet. */}
      {open && (
        <SavedDocsModal
          docType={docType}
          title={title}
          currentId={currentId}
          basePath={path ?? `/app/${docType}`}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function SavedDocsModal({
  docType,
  title,
  currentId,
  basePath,
  onClose,
}: {
  docType: string;
  title: string;
  currentId?: number | null;
  basePath: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data: list = [], isLoading, error } = useDocuments({ doc_type: docType });

  function openDoc(d: SavedDocument) {
    onClose();
    navigate(`${basePath}?id=${d.id}`);
  }

  function previewDoc(d: SavedDocument) {
    const url = `${basePath}?id=${d.id}&pdf=preview`;
    const w = window.open(url, '_blank');
    if (!w) {
      onClose();
      navigate(url); // prohlížeč zablokoval nové okno
    }
  }

  return (
    <Modal open onClose={onClose} title={title} size="lg">
      {isLoading && <div style={{ padding: 12, color: 'var(--sd-text-dim)' }}>Načítám…</div>}
      {error && (
        <div style={{ padding: 12, color: 'var(--sd-danger)' }}>
          Seznam se nepodařilo načíst. Zkontrolujte připojení a přihlášení.
        </div>
      )}
      {!isLoading && !error && list.length === 0 && (
        <div style={{ padding: 12, color: 'var(--sd-text-dim)' }}>
          Zatím tu nic není. Vyplněný dokument uložíte tlačítkem <strong>Uložit</strong> vpravo
          nahoře — pak se objeví tady i v Archivu.
        </div>
      )}

      {list.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((d) => {
            const isCurrent = currentId != null && d.id === currentId;
            return (
              <div
                key={d.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--sd-border)',
                  background: isCurrent ? 'var(--sd-yellow-soft)' : 'var(--sd-surface)',
                }}
              >
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{d.title || '(bez názvu)'}</div>
                  <div style={{ fontSize: 12, color: 'var(--sd-text-dim)' }}>
                    {[d.stavba, formatDateTime(d.updated_at)].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button size="sm" variant="ghost" onClick={() => previewDoc(d)}>
                    Náhled PDF
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => openDoc(d)}>
                    {isCurrent ? 'Otevřeno' : 'Otevřít'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
